import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
// Public/auth pages loaded eagerly — they are the first pages anonymous
// visitors see (Lighthouse audits them and we want zero Suspense flash).
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Verify from "@/pages/Verify";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Regulamin from "@/pages/legal/Regulamin";
import PolitykaPrywatnosci from "@/pages/legal/PolitykaPrywatnosci";
import UmowaPowierzenia from "@/pages/legal/UmowaPowierzenia";
// Authenticated pages code-split so anonymous visitors don't download
// them. Reduces initial JS by ~60%.
const Layout = lazy(() => import("@/components/Layout"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Habits = lazy(() => import("@/pages/Habits"));
const Workouts = lazy(() => import("@/pages/Workouts"));
const Measurements = lazy(() => import("@/pages/Measurements"));
const Goals = lazy(() => import("@/pages/Goals"));
const Tasks = lazy(() => import("@/pages/Tasks"));
const Diet = lazy(() => import("@/pages/Diet"));
const Nauka = lazy(() => import("@/pages/Nauka"));
const Admin = lazy(() => import("@/pages/Admin"));
const Konto = lazy(() => import("@/pages/Konto"));
const Locked = lazy(() => import("@/pages/Locked"));
const PaymentsReturn = lazy(() => import("@/pages/PaymentsReturn"));
const Landing = lazy(() => import("@/pages/Landing"));
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useVisitTracker } from "@/lib/useVisitTracker";

function PageLoader() {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center" role="status" aria-live="polite">
      <div className="w-8 h-8 rounded-full border-2 border-stone-200 border-t-stone-900 animate-spin" />
      <span className="sr-only">Ładowanie…</span>
    </div>
  );
}

function GlobalTracker() {
  useVisitTracker();
  return null;
}

function GlobalLangSwitcher() {
  // Layout (used for authenticated pages) already renders its own switcher in
  // both mobile top-nav and desktop sidebar. Rendering the global one on top
  // of that produced a duplicate. Show the global one only for logged-out /
  // public pages (Landing, Login, Register, legal, verify, reset…).
  const { user } = useAuth();
  if (user) return null;
  return (
    <div className="fixed top-3 right-3 z-[100]" data-testid="global-lang-switcher">
      <LanguageSwitcher />
    </div>
  );
}

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-muted-foreground text-sm font-medium animate-pulse">
          Ładowanie…
        </div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.account?.locked) return <Locked />;
  return children;
}

function ProtectedAllowLocked({ children }) {
  // Same as Protected but does not redirect locked users — used for /payments/return
  const { user } = useAuth();
  if (user === null) return null;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return null;
  if (user) return <Navigate to="/" replace />;
  return children;
}

function RootRoute() {
  const { user } = useAuth();
  const location = useLocation();
  const isRoot = location.pathname === "/";

  // Auth still loading — for /: show Landing (perceived speed); for sub-paths: wait
  if (user === null && isRoot) return <Landing />;
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-muted-foreground text-sm font-medium animate-pulse">Ładowanie…</div>
      </div>
    );
  }

  // Logged out
  if (!user) {
    return isRoot ? <Landing /> : <Navigate to="/login" replace />;
  }

  // Logged in but locked (unverified / expired trial / no subscription)
  if (user.account?.locked) return <Locked />;

  // Logged in and active → render the app shell (with Outlet for nested routes)
  return <Layout />;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster
          position="top-right"
          theme="dark"
          toastOptions={{
            style: {
              background: "#0F0F0F",
              border: "1px solid #262626",
              color: "#fafafa",
              borderRadius: "2px",
              padding: "12px 16px",
              boxShadow: "0 12px 32px rgba(0,0,0,0.6)",
            },
          }}
        />
        <GlobalTracker />
        <GlobalLangSwitcher />
        <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
          <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
          <Route path="/verify" element={<Verify />} />
          <Route path="/forgot-password" element={<PublicOnly><ForgotPassword /></PublicOnly>} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route
            path="/payments/return"
            element={
              <ProtectedAllowLocked>
                <PaymentsReturn />
              </ProtectedAllowLocked>
            }
          />
          <Route path="/regulamin" element={<Regulamin />} />
          <Route path="/polityka-prywatnosci" element={<PolitykaPrywatnosci />} />
          <Route path="/umowa-powierzenia" element={<UmowaPowierzenia />} />
          <Route path="/" element={<RootRoute />}>
            <Route index element={<Dashboard />} />
            <Route path="nawyki" element={<Habits />} />
            <Route path="silownia" element={<Workouts />} />
            <Route path="pomiary" element={<Measurements />} />
            <Route path="cele" element={<Goals />} />
            <Route path="zadania" element={<Tasks />} />
            <Route path="dieta" element={<Diet />} />
            <Route path="nauka" element={<Nauka />} />
            <Route path="konto" element={<Konto />} />
            <Route path="admin" element={<Admin />} />
          </Route>
        </Routes>
        </Suspense>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
